import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Table, TableColumn } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntegrationEnvVariable } from '@/entities/integrations';

type Props = {
  envVariables: IntegrationEnvVariable[];
};

export const EnvVariablesSection: FC<Props> = ({ envVariables }) => {
  const { t } = useTranslation(['integrations', 'common']);

  const hasUnfilledRequired = useMemo(() => envVariables.some((v) => v.required && !v.isFilled), [envVariables]);

  const columns: TableColumn<IntegrationEnvVariable>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: () => <span>{t('integrations:envVariables.table.name')}</span>,
        size: 260,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: () => <span>{t('integrations:envVariables.table.description')}</span>,
        cell: ({ row }) => <span className="text-sm">{row.original.description}</span>,
      },
      {
        accessorKey: 'required',
        header: () => <span>{t('integrations:envVariables.table.required')}</span>,
        size: 140,
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.required ? t('integrations:envVariables.table.yes') : t('integrations:envVariables.table.no')}
          </span>
        ),
      },
      {
        accessorKey: 'isFilled',
        header: () => <span>{t('integrations:envVariables.table.status')}</span>,
        size: 120,
        cell: ({ row }) => (
          <Badge variant={row.original.isFilled ? 'primary' : 'destructive'}>
            {row.original.isFilled
              ? t('integrations:envVariables.table.filled')
              : t('integrations:envVariables.table.notFilled')}
          </Badge>
        ),
      },
    ],
    [t],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{t('integrations:envVariables.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {hasUnfilledRequired ? (
          <Alert type="warning" title={t('integrations:envVariables.warningAlert')} />
        ) : (
          <Alert type="success" title={t('integrations:envVariables.successAlert')} />
        )}
        <Table data={envVariables} columns={columns} getKey={(row) => row.name} styles={{ size: 'sm' }} />
      </CardContent>
    </Card>
  );
};
